import React, { useState } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, IconButton
} from "@material-tailwind/react";
import { CheckCircleIcon, EyeIcon, XMarkIcon } from "@heroicons/react/24/outline";

function parseShipmentText(text) {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);

    let royalMailOrderNumber = '';
    let weight = 0;
    let shippingFees = 0;
    let parcelForwardingFees = 0;
    let yugotransportInternalNumber = '';
    let yugotransportOrderNumber = '';
    const items = [];

    for (const line of lines) {
        if (line.startsWith('RoyalMail Order Number:')) {
            royalMailOrderNumber = line.split(':').slice(1).join(':').trim();
        } else if (line.startsWith('Weight:')) {
            weight = parseFloat(line.replace('Weight:', '').replace('kg', '').trim());
        } else if (line.startsWith('Shipping Fees:')) {
            shippingFees = parseFloat(line.replace('Shipping Fees:', '').replace('£', '').trim());
        } else if (line.startsWith('Parcel Forwarding Fees:')) {
            parcelForwardingFees = parseFloat(line.replace('Parcel Forwarding Fees:', '').replace('£', '').trim());
        } else if (line.startsWith('Yugotransport Internal Number:')) {
            yugotransportInternalNumber = line.split(':').slice(1).join(':').trim();
        } else if (line.startsWith('Yugotransport Order Number:')) {
            yugotransportOrderNumber = line.split(':').slice(1).join(':').trim();
        } else {
            const itemMatch = line.match(/^-\s*(.+?)\s*-\s*(\d+)g$/);
            if (itemMatch) {
                items.push({ title: itemMatch[1].trim(), weightG: parseInt(itemMatch[2]) });
            }
        }
    }

    if (!royalMailOrderNumber) throw new Error('RoyalMail Order Number not found');
    if (items.length === 0) throw new Error('No items found');

    const totalItemsG = items.reduce((sum, i) => sum + i.weightG, 0);
    const boxWeightG = (weight * 1000) - totalItemsG;
    const boxPerItemG = boxWeightG / items.length;
    const totalEffectiveG = weight * 1000;

    const processedItems = items.map(item => {
        const effectiveWeightG = item.weightG + boxPerItemG;
        const pct = effectiveWeightG / totalEffectiveG;
        return {
            title: item.title,
            weightG: item.weightG,
            effectiveWeightG: Math.round(effectiveWeightG * 100) / 100,
            shippingFees: Math.round(pct * shippingFees * 100) / 100,
            parcelForwardingFees: Math.round(pct * parcelForwardingFees * 100) / 100,
        };
    });

    return {
        royalMailOrderNumber, weight, shippingFees, parcelForwardingFees,
        yugotransportInternalNumber, yugotransportOrderNumber, items: processedItems
    };
}

export default function ShipmentDialog({ isOpen, onClose, onStartProcessing }) {

    const [shipmentTextInput, setShipmentTextInput] = useState('');
    const [parsedShipmentData, setParsedShipmentData] = useState(null);
    const [shipmentParseError, setShipmentParseError] = useState('');

    const handleClose = () => {
        setShipmentTextInput('');
        setParsedShipmentData(null);
        setShipmentParseError('');
        onClose();
    };

    const handleStartProcessing = () => {
        onStartProcessing(parsedShipmentData);
        setShipmentTextInput('');
        setParsedShipmentData(null);
        setShipmentParseError('');
        onClose();
    };

    return (
        <Dialog size="l" open={isOpen} handler={handleClose}
                className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden">
            <DialogHeader className="relative m-0 block">
                <Typography variant="h4" color="blue-gray">Process Shipment</Typography>
                <Typography className="mt-1 font-normal text-gray-600">Paste your shipment data to automatically update items.</Typography>
                <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={handleClose}>
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>
            <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                <div>
                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Shipment Data</Typography>
                    <textarea
                        value={shipmentTextInput}
                        onChange={(e) => setShipmentTextInput(e.target.value)}
                        placeholder={`RoyalMail Order Number: YA629795961GB:\nWeight: 6.1 kg\nShipping Fees: £17.5\nParcel Forwarding Fees: £18.95\nYugotransport Internal Number: YUP275711\nYugotransport Order Number: 130\n- Vagabond (VIZBIG Edition), Vol. 1 - 1221g\n- Vinland Saga, Vol. 5 - 620g`}
                        className="w-full h-64 p-3 border border-gray-300 rounded-lg resize-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm font-mono dark:bg-dark-800 dark:border-gray-600 dark:text-gray-300"
                        rows={10}
                    />
                </div>
                {parsedShipmentData && (
                    <div className="bg-gray-50 dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <Typography variant="h6" className="mb-2 dark:text-gray-300">Preview:</Typography>
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                            <div><span className="text-gray-500 dark:text-gray-400">RoyalMail:</span> <span className="font-medium dark:text-gray-300">{parsedShipmentData.royalMailOrderNumber}</span></div>
                            <div><span className="text-gray-500 dark:text-gray-400">Weight:</span> <span className="font-medium dark:text-gray-300">{parsedShipmentData.weight} kg</span></div>
                            <div><span className="text-gray-500 dark:text-gray-400">Shipping Fees:</span> <span className="font-medium dark:text-gray-300">£{parsedShipmentData.shippingFees}</span></div>
                            <div><span className="text-gray-500 dark:text-gray-400">Parcel Fees:</span> <span className="font-medium dark:text-gray-300">£{parsedShipmentData.parcelForwardingFees}</span></div>
                            <div><span className="text-gray-500 dark:text-gray-400">Yug Internal:</span> <span className="font-medium dark:text-gray-300">{parsedShipmentData.yugotransportInternalNumber}</span></div>
                            <div><span className="text-gray-500 dark:text-gray-400">Yug Order:</span> <span className="font-medium dark:text-gray-300">{parsedShipmentData.yugotransportOrderNumber}</span></div>
                            <div><span className="text-gray-500 dark:text-gray-400">Items:</span> <span className="font-medium dark:text-gray-300">{parsedShipmentData.items.length}</span></div>
                        </div>
                    </div>
                )}
                {shipmentParseError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <Typography variant="small" className="text-red-700 dark:text-red-300 font-medium">⚠️ {shipmentParseError}</Typography>
                    </div>
                )}
            </DialogBody>
            <DialogFooter className="flex items-center justify-between gap-2">
                <Button
                    onClick={() => {
                        try {
                            const data = parseShipmentText(shipmentTextInput);
                            setParsedShipmentData(data);
                            setShipmentParseError('');
                        } catch (err) {
                            setParsedShipmentData(null);
                            setShipmentParseError(err.message);
                        }
                    }}
                    className="mv-secondary-button"
                >
                    <EyeIcon className="h-4 w-4" />
                    Preview
                </Button>
                <Button onClick={handleStartProcessing} disabled={!parsedShipmentData} className="mv-primary-button">
                    <CheckCircleIcon className="h-4 w-4" />
                    Start Processing {parsedShipmentData ? `(${parsedShipmentData.items.length} items)` : ''}
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
